import { inject } from '@angular/core';
import { CanMatchFn, Router } from '@angular/router';
import { catchError, map, of, switchMap } from 'rxjs';
import { OrganizationService } from '../../services/organization/organization.service';
import { PermissionService } from '../../services/permission/permission.service';
import { PortalService } from '../../services/portal/portal.service';
import { UserService } from '../../services/user/user.service';
import { Permission } from '../../enums/permission.enum';
import { isSubscriptionActive } from '../../utils/is-subscription-active.util';

/** Route the user is sent to when their organization has no active subscription. */
export const PLAN_SELECTION_ROUTE = '/onboarding/plan';

/** Route for members who cannot manage billing when the subscription is inactive. */
export const SUBSCRIPTION_LOCKED_ROUTE = '/onboarding/locked';

/**
 * Blocks routes that require an active subscription. When the organization has no
 * access-granting subscription (the same state the backend answers with 402), the user
 * is redirected by what they can actually do about it:
 *
 * - has customer-portal relations → the customer portal (a no-subscription buyer
 *   account lives there; the invited-customer accounts never bought a plan);
 * - can manage billing → plan selection (they can renew / pick a plan);
 * - cannot manage billing → the locked screen ("ask your administrator");
 * - no membership at all (e.g. revoked) → organization creation, with a notice -
 *   checked *before* any org-scoped call, which would otherwise 403 and surface
 *   a raw error.
 *
 * Uses the cache-backed observables so the check does not depend on signals being
 * pre-populated before the guard runs.
 */
export const subscriptionGuard: CanMatchFn = () => {
  const userService = inject(UserService);
  const organizationService = inject(OrganizationService);
  const permissionService = inject(PermissionService);
  const portalService = inject(PortalService);
  const router = inject(Router);

  return userService.getCurrentUser().pipe(
    switchMap((user) => {
      if (user.memberships.length === 0) {
        return of(
          router.createUrlTree(['/organizations/create'], {
            queryParams: { notice: 'no-organization' },
          }),
        );
      }

      return organizationService.getCurrentOrganization().pipe(
        switchMap((org) => {
          if (isSubscriptionActive(org)) return of(true);

          return portalService.getSuppliers().pipe(
            catchError(() => of([])),
            map((suppliers) => {
              if (suppliers.length > 0) return router.createUrlTree(['/portal']);

              const canManageBilling = permissionService.canWithOrg(org, Permission.BillingsManage);
              return router.createUrlTree([
                canManageBilling ? PLAN_SELECTION_ROUTE : SUBSCRIPTION_LOCKED_ROUTE,
              ]);
            }),
          );
        }),
      );
    }),
    catchError(() => of(router.createUrlTree([PLAN_SELECTION_ROUTE]))),
  );
};
